/**
 * Centralized Error Handling System
 * Reduces console noise and provides structured error logging
 */

type ErrorLevel = 'info' | 'warn' | 'error' | 'critical';

interface ErrorLog {
  level: ErrorLevel;
  message: string;
  context?: string;
  timestamp: number;
  error?: Error;
}

class ErrorHandler {
  private logs: ErrorLog[] = [];
  private maxLogs = 100;
  private isDevelopment = process.env.NODE_ENV === 'development';
  private verboseMode = process.env.REACT_APP_VERBOSE_LOGS === 'true';

  /**
   * Log an error with context
   */
  log(level: ErrorLevel, message: string, context?: string, error?: Error): void {
    const log: ErrorLog = {
      level,
      message,
      context,
      timestamp: Date.now(),
      error
    };

    // Add to internal log buffer
    this.logs.push(log);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Only log to console in development or if critical
    const shouldLog = this.isDevelopment || level === 'critical' || this.verboseMode;

    if (shouldLog) {
      const prefix = context ? `[${context}]` : '';
      const formattedMessage = `${prefix} ${message}`;

      switch (level) {
        case 'info':
          console.log(`ℹ️ ${formattedMessage}`, error || '');
          break;
        case 'warn':
          console.warn(`⚠️ ${formattedMessage}`, error || '');
          break;
        case 'error':
          console.error(`❌ ${formattedMessage}`, error || '');
          break;
        case 'critical':
          console.error(`🔥 CRITICAL: ${formattedMessage}`, error || '');
          break;
      }
    }
  }

  /**
   * Get recent error logs
   */
  getLogs(level?: ErrorLevel): ErrorLog[] {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return [...this.logs];
  }

  /**
   * Clear all logs
   */
  clear(): void {
    this.logs = [];
  }

  /**
   * Check if there are critical errors
   */
  hasCriticalErrors(): boolean {
    return this.logs.some(log => log.level === 'critical');
  }

  /**
   * Get error count by level
   */
  getErrorCount(level?: ErrorLevel): number {
    if (level) {
      return this.logs.filter(log => log.level === level).length;
    }
    return this.logs.length;
  }
}

// Singleton instance
export const errorHandler = new ErrorHandler();

// Convenience methods
export const logInfo = (message: string, context?: string) => 
  errorHandler.log('info', message, context);

export const logWarn = (message: string, context?: string, error?: Error) => 
  errorHandler.log('warn', message, context, error);

export const logError = (message: string, context?: string, error?: Error) => 
  errorHandler.log('error', message, context, error);

export const logCritical = (message: string, context?: string, error?: Error) => 
  errorHandler.log('critical', message, context, error);
